import { useEffect, useState } from "react";
import { Loader2, ShieldAlert, Ban, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatErr, fmtDate } from "@/lib/api";
import { UserAvatar } from "@/components/UserBits";

const Mini = ({ u, tag }) => (
  <div className="flex items-center gap-2 min-w-0">
    <UserAvatar userId={u.user_id} name={u.name} picture={u.picture} size="w-8 h-8" text="text-xs" noStory />
    <div className="min-w-0"><p className="text-sm font-semibold truncate">{u.name}{u.banned && <span className="ml-1 text-[10px] text-rose-600 font-bold">GESPERRT</span>}</p><p className="text-[11px] text-slate-400 truncate">{tag || u.email}</p></div>
  </div>
);

export function FakeFlagsAdmin() {
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState(null);
  useEffect(() => { api.get("/admin/fake-flags").then(({ data }) => setRows(data)).catch((e) => toast.error(formatErr(e))); }, []);
  const decide = async (r, action) => {
    if (action === "ban" && !window.confirm(`${r.name} wirklich sperren? Der Einlader verliert die Punkte für diese Einladung.`)) return;
    setBusy(r.user_id);
    try {
      await api.post(`/admin/fake-flags/${r.user_id}`, { action });
      setRows((l) => l.filter((x) => x.user_id !== r.user_id));
      toast.success(action === "ban" ? `${r.name} wurde gesperrt` : `${r.name} ist als echt markiert`);
    } catch (e) { toast.error(formatErr(e)); } finally { setBusy(null); }
  };
  return (
    <div className="rounded-3xl bg-white border border-pink-100 p-5 space-y-4" data-testid="fake-flags-admin">
      <div>
        <h3 className="font-bold text-lg flex items-center gap-2"><ShieldAlert className="w-5 h-5 text-rose-500" /> Verdächtige Konten <span className="text-sm text-slate-400">({rows?.length ?? "…"})</span></h3>
        <p className="text-xs text-slate-500 mt-1">Diese Konten nutzen dieselbe IP-Adresse <b>und</b> dasselbe Gerät wie ein älteres Konto. Bis du entscheidest, zählen sie nicht als eingeladene Freunde.</p>
      </div>
      {!rows ? <Loader2 className="w-5 h-5 animate-spin text-rose-400 mx-auto" /> : rows.length === 0 ? <p className="text-sm text-slate-400 text-center py-4" data-testid="fake-flags-empty">Keine offenen Verdachtsfälle 🎉</p> : (
        <ul className="space-y-3">
          {rows.map((r) => (
            <li key={r.user_id} className="rounded-2xl border border-rose-100 bg-rose-50/40 p-3 space-y-3" data-testid={`fake-flag-${r.user_id}`}>
              <div className="flex items-start justify-between gap-3">
                <Mini u={r} tag={`${r.email} · seit ${fmtDate(r.created_at)}`} />
                <span className="shrink-0 text-[10px] font-extrabold uppercase text-rose-600 bg-white rounded-full px-2 py-0.5 border border-rose-200">Gleiche IP + Gerät</span>
              </div>
              <div className="pl-2 border-l-2 border-rose-200 space-y-2">
                <p className="text-[11px] font-bold text-slate-500">Übereinstimmung mit:</p>
                {r.matches.map((m) => <Mini key={m.user_id} u={m} />)}
                {r.referrer && <p className="text-[11px] text-slate-500">Eingeladen von <b>{r.referrer.name}</b></p>}
              </div>
              <div className="grid grid-cols-2 gap-2">
                <button disabled={busy === r.user_id} onClick={() => decide(r, "ban")} data-testid={`fake-flag-ban-${r.user_id}`} className="h-10 rounded-full bg-rose-600 text-white text-sm font-bold inline-flex items-center justify-center gap-1.5 disabled:opacity-60"><Ban className="w-4 h-4" /> Sperren</button>
                <button disabled={busy === r.user_id} onClick={() => decide(r, "clear")} data-testid={`fake-flag-clear-${r.user_id}`} className="h-10 rounded-full bg-white border border-emerald-200 text-emerald-700 text-sm font-bold inline-flex items-center justify-center gap-1.5 disabled:opacity-60"><CheckCircle2 className="w-4 h-4" /> Ist echt</button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
